import { Authority, useAuthenticated } from "providers/A12n"
import { CellProps, Column } from "react-table"
import { Client, ClientAgencyConfig } from "models/common"

import { ReactComponent as CheckIcon } from "assets/check.svg"
import { ColorMap } from "utils/color"
import { ReactComponent as DeactivateIcon } from "assets/icon-deactivate.svg"
import { Link } from "components/Link"
import MoreActions from "components/MoreActions"
import { Status } from "components/Status"
import { useI18n } from "providers/I18n"

type GetClientColumns = (
  onActivate: (id: string) => void,
  onDeactivate: (id: string) => void,
  hasAuthority: (allowed: Authority[]) => boolean,
) => Array<Column<Client>>

type GetClientConfigColumns = (
  onActivate: (id: string) => void,
  onDeactivate: (id: string) => void,
  hasAuthority: (allowed: Authority[]) => boolean,
) => Array<Column<ClientAgencyConfig>>

export const clientColumns: GetClientColumns = (
  onActivate,
  onDeactivate,
  hasAuthority,
) => {
  const agency_columns = [
    {
      fixed: true,
      default: true,
      align: "right",
      sortable: false,
      header: "clients.list.actions",
      id: "actions",
      Cell: function (props: CellProps<Client>) {
        const { t } = useI18n()
        const { id, configStatus } = props.row.original
        const canActivate = configStatus !== "ACTIVE"
        const { hasAuthority } = useAuthenticated()

        return (
          <MoreActions
            options={[
              ...[
                canActivate
                  ? {
                      hidden: !hasAuthority(["AUTH_ACTIVATE_AGENCY_CLIENT"]),
                      label: t("agency.client.activate.action"),
                      icon: <CheckIcon />,
                      onClick: () => {
                        onActivate(id)
                      },
                    }
                  : {
                      hidden: !hasAuthority(["AUTH_DEACTIVATE_AGENCY_CLIENT"]),
                      label: t("agency.client.deactivate.action"),
                      icon: <DeactivateIcon />,
                      onClick: () => {
                        onDeactivate(id)
                      },
                    },
              ],
            ]}
          />
        )
      },
    },
  ]

  let custom_columns = [] as any
  if (!hasAuthority(["AUTH_ACCESS_AGENCY_ADMIN"])) {
    custom_columns = agency_columns
  }

  return [
    {
      fixed: true,
      default: true,
      sortable: false,
      header: "clients.list.name",
      id: "name",
      accessor: "name",
      Cell: function (props: CellProps<Client>) {
        const { id, name } = props.row.original
        return (
          <Link color="primary" to={`/agency-clients/${id}`}>
            {name}
          </Link>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.status",
      id: "status",
      accessor: "configStatus",
      Cell: function (props: CellProps<Client>) {
        const { value: configStatus } = props
        const { t } = useI18n()
        return configStatus === null ? null : (
          <Status color={ColorMap[configStatus]}>
            {t(`status.${configStatus ?? ""}`)}
          </Status>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.companies",
      id: "companies",
      Cell: function (props: CellProps<Client>) {
        const { companies } = props.row.original
        return (companies || [])?.map((c, cidx) => (
          <div key={cidx}>{`${c?.name} - ${c?.clientCode}`}</div>
        ))
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.client-groups",
      id: "groupName",
      accessor: "groupName",
    },
    ...custom_columns,
  ]
}

export const clientConfigColumns: GetClientConfigColumns = (
  onActivate,
  onDeactivate,
  hasAuthority,
) => {
  const agency_columns = [
    {
      fixed: true,
      default: true,
      align: "right",
      sortable: false,
      header: "clients.list.actions",
      id: "actions",
      Cell: function (props: CellProps<ClientAgencyConfig>) {
        const { t } = useI18n()
        const { id, status } = props.row.original
        const canActivate = status !== "ACTIVE"
        const { hasAuthority } = useAuthenticated()

        return (
          <MoreActions
            options={[
              ...[
                canActivate
                  ? {
                      hidden: !hasAuthority(["AUTH_ACTIVATE_AGENCY_CLIENT"]),
                      label: t("agency.client.activate.action"),
                      icon: <CheckIcon />,
                      onClick: () => {
                        onActivate(id)
                      },
                    }
                  : {
                      hidden: !hasAuthority(["AUTH_DEACTIVATE_AGENCY_CLIENT"]),
                      label: t("agency.client.deactivate.action"),
                      icon: <DeactivateIcon />,
                      onClick: () => {
                        onDeactivate(id)
                      },
                    },
              ],
            ]}
          />
        )
      },
    },
  ]

  let custom_columns = [] as any
  if (!hasAuthority(["AUTH_ACCESS_AGENCY_ADMIN"])) {
    custom_columns = agency_columns
  }

  return [
    {
      fixed: true,
      default: true,
      sortable: false,
      header: "clients.list.name",
      id: "clientName",
      accessor: "clientName",
      Cell: function (props: CellProps<ClientAgencyConfig>) {
        const { clientName } = props.row.original
        return (
          <Link color="primary" to="#">
            {clientName}
          </Link>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.status",
      id: "status",
      accessor: "status",
      Cell: function (props: CellProps<ClientAgencyConfig>) {
        const { value: status } = props
        const { t } = useI18n()
        return status === null ? null : (
          <Status color={ColorMap[status]}>
            {t(`status.${status ?? ""}`)}
          </Status>
        )
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.companies",
      id: "companies",
      Cell: function (props: CellProps<Client>) {
        const { companies } = props.row.original
        return (companies || [])?.map((c, cidx) => (
          <div key={cidx}>{`${c?.name} - ${c?.clientCode}`}</div>
        ))
      },
    },
    {
      default: true,
      sortable: false,
      header: "clients.list.agency-name",
      id: "agencyName",
      accessor: "agencyName",
    },
    ...custom_columns,
  ]
}

export const filters = []
export const sortByDefault = "name,ASC"
